import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import { DollarSign, Clock, MapPin, Camera, HardHat } from "lucide-react";

const PRIORITY_COLORS = {
  urgent: "bg-rose-500/20 text-rose-300 border-rose-500/40",
  high: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  medium: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  low: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
};

export default function VendorTicketsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [eta, setEta] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/vendor/tickets");
      setItems(data.items);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/vendor/VendorTicketsPage.jsx: swallowed', err); }
  };
  useEffect(() => { load(); }, []);

  const openBid = (t) => {
    setSelected(t);
    setAmount(t.my_bid?.amount ?? "");
    setNote(t.my_bid?.note ?? "");
    setEta(t.my_bid?.eta_days ?? "");
  };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      await api.post(`/maintenance/${selected.id}/bids`, {
        amount: Number(amount),
        note,
        eta_days: eta ? Number(eta) : null,
      });
      toast.success("Bid submitted");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSubmitting(false);
  };

  return (
    <div>
      <PageHeader eyebrow="Ticket Board" title="Open work orders" subtitle="Submit a bid — the resident (or their admin) picks the winner." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((t) => (
          <Card key={t.id} className="p-5 border-white/10 bg-card" data-testid={`vticket-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="overline">{t.category}</div>
                  <Badge variant="outline" className={PRIORITY_COLORS[t.priority]}>{t.priority}</Badge>
                  <div className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Unit {t.unit}</div>
                </div>
                <div className="text-lg font-semibold font-display mt-1">{t.title}</div>
                <div className="text-sm text-zinc-300 mt-1">{t.description}</div>
              </div>
              {t.assigned_vendor && (
                <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 shrink-0">
                  <HardHat className="h-3 w-3 mr-1 inline" />assigned
                </Badge>
              )}
            </div>

            {t.photos?.length ? (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                  <Camera className="h-3 w-3" /> Site photos ({t.photos.length})
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {t.photos.slice(0, 4).map((p) => (
                    <AuthImage key={p.path} path={p.path} className="aspect-square rounded-md object-cover border border-white/10" />
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-between">
              {t.my_bid ? (
                <div className="text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your bid</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold tabular-nums text-emerald-300">${Number(t.my_bid.amount).toFixed(2)}</span>
                    <Badge variant="outline" className="border-white/10 capitalize">{t.my_bid.status}</Badge>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500">No bid yet</div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => nav(`/vendor/tickets/${t.id}`)}
                  variant="outline"
                  className="rounded-full border-white/10 text-zinc-300"
                  data-testid={`view-btn-${t.id}`}
                >
                  Open
                </Button>
                <Button
                  onClick={() => openBid(t)}
                  disabled={t.assigned_vendor && t.assigned_vendor_id !== undefined && t.my_bid?.status !== "accepted"}
                  data-testid={`bid-btn-${t.id}`}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <DollarSign className="h-4 w-4 mr-1" /> {t.my_bid ? "Update bid" : "Submit bid"}
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">No open tickets in your assigned properties.</div>}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>{selected?.my_bid ? "Update bid" : "Submit bid"} · {selected?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (USD)</Label>
              <Input data-testid="bid-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>ETA (days)</Label>
              <Input data-testid="bid-eta" type="number" min="0" value={eta} onChange={(e) => setEta(e.target.value)} placeholder="e.g. 2" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea data-testid="bid-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What's included in your price" />
            </div>
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 text-xs p-3 flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Residents receive all bids in real time and can accept the winner with one tap.</span>
            </div>
            <Button data-testid="bid-submit" onClick={submit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {submitting ? "Submitting…" : (selected?.my_bid ? "Update bid" : "Submit bid")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
